import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Theme,
  Typography
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { makeStyles } from '@mui/styles';
import useAuth from 'hooks/useAuth';
import useTimezone from 'hooks/useTimezone';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactQuill from 'react-quill';
import { Rnd } from 'react-rnd';
import MainCard from 'views/components/cards/MainCard';
import usePatientSessionNotes from '../client-record/usePatientSessionNotes';
import { SessionData } from './useSessionData';

const useStyles = makeStyles((theme: Theme) => ({
  reviewDialog: {
    '&>div:nth-child(3)': {
      '&>div': {}
    }
  },
  dialogPaper: {
    padding: '4px'
  },
  reviewContainer: {
    marginTop: '0px',
    marginBottom: '0px'
  },
  resizableContent: {
    position: 'relative',
    '& .react-resizable-handle': {
      position: 'absolute',
      width: 20,
      height: 20,
      bottom: 0,
      right: 0,
      padding: '0 3px 3px 0',
      cursor: 'se-resize'
    }
  },
  addNoteButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    margin: '0 50px 0 0'
  },
  copyButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  copyButton: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  quillContainer: {
    '& .ql-editor': {
      minHeight: 100,
      height: `${window.innerHeight / 4.8}px !important`,
      border: '1px solid #ddd',
      borderRadius: '8px'
    }
  },
  noBorder: {
    '& .ql-container.ql-snow': {
      border: 'none !important'
    }
  },
  noteDetailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    padding: theme.spacing(2),
    marginTop: '6px',
    border: '1px solid #ddd',
    borderRadius: '25px',
    height: `${window.innerHeight / 7.5}px !important`
  },
  noNotesMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    minWidth: '50vw'
  }
}));

interface Props {
  onClose: () => void;
  sessionData: SessionData;
}

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return `${date.toLocaleDateString()}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const validateBody = (body: string) => {
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

const handleCopyToClipboard = (htmlContent: string | undefined, enqueueSnackbar: any, intl: any) => {
  if (htmlContent) {
    const plainText = htmlContent.replace(/<\/?[^>]+(>|$)/g, '');

    navigator.clipboard
      .writeText(plainText)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Copied to clipboard!' }), { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ defaultMessage: 'Failed to copy!' }), { variant: 'error' });
      });
  }
};

const NoteDialog: React.FC<{ note: any; open: boolean; onClose: () => void }> = ({ note, open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{note?.title}</span>
          <div>
            <Button
              onClick={() => handleCopyToClipboard(note?.body, enqueueSnackbar, intl)}
              color="primary"
              style={{ marginRight: '8px' }}
              startIcon={<ContentCopyIcon />}
            >
              <FormattedMessage defaultMessage="Copy Note" />
            </Button>
            <Button onClick={onClose} color="secondary" startIcon={<CloseFullscreenIcon />}></Button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <ReactQuill
          theme="snow"
          value={validateBody(note?.body)}
          readOnly={true}
          modules={{ toolbar: false }}
          style={{ height: '400px' }}
        />
      </DialogContent>
    </Dialog>
  );
};

interface Props {
  onClose: () => void;
  sessionData: SessionData;
  onCompleteClose: () => void;
}

const PreviewsNoteSession: React.FC<Props> = ({ onClose, sessionData, onCompleteClose }) => {
  const classes = useStyles();
  const { userId } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { timezone } = useTimezone();
  const intl = useIntl();
  const [notesWidth, setNotesWidth] = useState(1120);
  const [notesHeight, setNotesHeight] = useState(590);
  const [selectedNote, setSelectedNote] = useState<any | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const { notes, refetchNotes } = usePatientSessionNotes(userId, selectedClientId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<any | null>(null);
  const quillRefs = useRef<ReactQuill[]>([]);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  useEffect(() => {
    if (sessionData.participants.length > 0) {
      const firstClient = sessionData?.participants.find((p) => p?.user?.isClient);
      if (firstClient) {
        setSelectedClientId(firstClient?.user?._id);
      }
    }
  }, [sessionData]);

  useEffect(() => {
    if (selectedClientId) {
      refetchNotes();
    }
  }, [selectedClientId, refetchNotes]);

  useEffect(() => {
    if (selectedNote && selectedNote?.participants?.length > 0) {
      const firstProvider = selectedNote?.participants.find((p) => p?.user?.role !== 'CLIENT');
      if (firstProvider) {
        setSelectedTab(firstProvider?.user?._id);
      }
    }
  }, [selectedNote, selectedClientId]);

  useEffect(() => {
    if (notes && notes.length > 0) {
      const sortedNotes = [...notes].sort(
        (a, b) => new Date(b?.sessionDate as string).getTime() - new Date(a?.sessionDate as string).getTime()
      );
      setSelectedNote(sortedNotes[0]);
    } else {
      setSelectedNote(null);
    }
  }, [notes]);

  const handleClientClick = (clientId: string) => {
    setSelectedClientId(clientId);
  };

  const handleEnlarge = (note: any) => {
    setCurrentNote(note);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentNote(null);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const renderNoteList = useCallback(() => {
    if (!notes) {
      return (
        <MainCard sx={{ height: '100%' }} headerSX={{ padding: 2 }} contentSX={{ padding: 0, height: '100%' }}>
          <Box
            sx={{
              overflowY: 'auto',
              maxHeight: notesHeight * 0.75,
              paddingBottom: 5
            }}
            m={0}
            p={1}
          >
            <LinearProgress />
          </Box>
        </MainCard>
      );
    }

    if (!notes.length) {
      return (
        <MainCard sx={{ height: '100%' }} headerSX={{ padding: 2 }} contentSX={{ padding: 0, height: '100%' }}>
          <ListItemButton
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 2,
              mb: 1,
              borderRadius: 2,
              boxShadow: '0 0 0 2px #1976d2',
              border: '1px solid #ddd',
              backgroundColor: '#e3f2fd',
              margin: '16px',
              height: '100px'
            }}
          >
            <Typography variant="body2">
              <strong>
                {' '}
                <FormattedMessage defaultMessage="No Notes Available" />
              </strong>
            </Typography>
          </ListItemButton>
        </MainCard>
      );
    }

    const sortedNotes = [...notes].sort(
      (a, b) => new Date(b?.sessionDate as string).getTime() - new Date(a?.sessionDate as string).getTime()
    );

    return (
      <MainCard sx={{ height: '100%' }} headerSX={{ padding: 2 }} contentSX={{ padding: 0, height: '100%' }}>
        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: notesHeight * 0.75,
            paddingBottom: 5
          }}
          m={0}
          p={1}
        >
          <List disablePadding sx={{ pb: 4 }}>
            {sortedNotes.map(
              (note) =>
                note?.sessionId && (
                  <ListItemButton
                    key={note?._id}
                    selected={note.sessionId === selectedNote?.sessionId}
                    onClick={() => setSelectedNote(note)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: 2,
                      mb: 1,
                      borderRadius: 2,
                      boxShadow: note?._id === selectedNote?.sessionId ? '0 0 0 2px #1976d2' : 'none',
                      border: '1px solid #ddd',
                      backgroundColor: note?._id === selectedNote?.sessionId ? '#e3f2fd' : 'white'
                    }}
                  >
                    <Typography variant="body2">
                      <strong>
                        {' '}
                        <FormattedMessage defaultMessage="Date & Time" />
                        :-
                      </strong>{' '}
                      {note && formatDateTime(note?.sessionDate as string)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>
                        {' '}
                        <FormattedMessage defaultMessage="Service Types" />
                        :-
                      </strong>{' '}
                      {note?.serviceType?.name || 'Unknown Service'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>
                        {' '}
                        <FormattedMessage defaultMessage="Provider:" />{' '}
                      </strong>
                      {note?.participants && note?.participants.length
                        ? note.participants
                            .filter((p: any) => p?.user?.role !== 'CLIENT')
                            .map((p: any) => `${p?.user?.firstName} ${p?.user?.lastName}`)
                            .join(', ')
                        : '-'}
                    </Typography>
                  </ListItemButton>
                )
            )}
          </List>
        </Box>
      </MainCard>
    );
  }, [notes, selectedNote, notesHeight]);

  return (
    <>
      <Rnd
        minWidth={1120}
        minHeight={590}
        style={{ zIndex: 1100, backgroundColor: 'white', border: '1px solid grey', borderRadius: '15px' }}
        onResize={(e, direction, ref, delta, position) => {
          setNotesWidth(ref.offsetWidth);
          setNotesHeight(ref.offsetHeight);
        }}
        cancel=".main-editor-custom"
      >
        <Box
          sx={{
            width: notesWidth,
            height: notesHeight
          }}
        >
          <Grid container item xs={12} sx={{ marginLeft: '16px' }}>
            <Grid item xs={6}>
              <Box sx={{ paddingTop: 2.1, paddingBottom: 1.8, paddingLeft: 1.2, display: 'flex', direction: 'row' }}>
                <ArrowBackIcon onClick={onClose} sx={{ cursor: 'pointer' }} />{' '}
                <Typography variant="h2" sx={{ px: 0.5 }}>
                  <FormattedMessage defaultMessage="Notes History" />
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={5.6} sx={{ display: 'flex', direction: 'rtl' }}>
              <IconButton onClick={onCompleteClose} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Grid container item xs={12} sx={{ px: 3, paddingTop: 1, paddingBottom: 0.5 }}>
            <Grid item xs={2.9}>
              <Typography variant="h4">
                {' '}
                <FormattedMessage defaultMessage="Current Attendees" />
              </Typography>
            </Grid>
            <Grid item xs={5.9}>
              <Typography variant="h4" style={{ paddingLeft: '8px' }}>
                <FormattedMessage defaultMessage="Session Info" />:{selectedNote && formatDateTime(selectedNote?.sessionDate)}
              </Typography>
            </Grid>
            <Grid item xs={2.8} sx={{ paddingLeft: 4.5 }}>
              <Typography variant="h4">
                {' '}
                <FormattedMessage defaultMessage="Previous Sessions" />
              </Typography>
            </Grid>
          </Grid>
          <Grid container xs={12} sx={{ px: 3 }}>
            <Grid item xs={2.8}>
              <MainCard headerSX={{ padding: 2 }} contentSX={{ padding: 0 }}>
                <Box sx={{ flexGrow: 1, height: notesHeight - 132 }}>
                  <List disablePadding sx={{ pb: 4 }}>
                    {sessionData?.participants
                      .filter((p) => p?.user?.isClient)
                      .map((participant, index) => (
                        <ListItemButton
                          key={index}
                          onClick={() => handleClientClick(participant?.user._id)}
                          selected={participant?.user?._id === selectedClientId}
                        >
                          <ListItemAvatar>
                            <Avatar>{participant?.user?.firstName.charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={`${participant?.user?.firstName} ${participant?.user?.lastName}`} />
                        </ListItemButton>
                      ))}
                  </List>
                </Box>
              </MainCard>
            </Grid>
            <Grid item xs={6.4} sx={{ px: 2 }}>
              {selectedNote && selectedNote?.notes && selectedNote?.notes.length > 0 ? (
                <MainCard headerSX={{ padding: 2 }} contentSX={{ padding: 0 }}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="providers tabs">
                          {selectedNote && selectedNote?.participants && selectedNote?.participants.length > 0 ? (
                            selectedNote?.participants
                              .filter((p) => p?.user?.role !== 'CLIENT')
                              .map((provider, index) => (
                                <Tab
                                  key={index}
                                  value={provider?.user?._id}
                                  label={`${provider?.user?.firstName} ${provider?.user?.lastName}`}
                                  sx={{ width: '141px', height: '42px' }}
                                />
                              ))
                          ) : (
                            <Tab label="No Providers" disabled sx={{ width: '141px', height: '42px' }} />
                          )}
                        </Tabs>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ height: notesHeight - 182, overflow: 'auto' }}>
                    {selectedNote.notes.length > 0 && selectedNote.notes.filter((p) => p?.ownerId?._id === selectedTab).length > 0 ? (
                      selectedNote.notes
                        .filter((p) => p?.ownerId?._id === selectedTab)
                        .sort((a: any, b: any) => {
                          const dateA = new Date(a?.date_modified).getTime();
                          const dateB = new Date(b?.date_modified).getTime();
                          return dateB - dateA;
                        })
                        .map((note, index) => (
                          <Grid key={index}>
                            <Grid
                              sx={{
                                display: 'flex',
                                flexDirection: 'column'
                                // border: '1px solid #ddd',
                              }}
                            >
                              <Grid item xs={12} sx={{ display: 'flex' }}>
                                <Grid item xs={8}>
                                  <Typography variant="h4" sx={{ p: 2 }}>
                                    {note.title}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ display: 'flex', direction: 'row', justifyContent: 'space-evenly' }}>
                                  <Typography
                                    variant="body2"
                                    className={classes.copyButton}
                                    onClick={() =>
                                      handleCopyToClipboard(quillRefs.current[index]?.getEditor().getText(), enqueueSnackbar, intl)
                                    }
                                  >
                                    <ContentCopyIcon fontSize="small" /> <FormattedMessage defaultMessage="Copy Note" />
                                  </Typography>
                                  <Typography variant="body2" className={classes.copyButton} onClick={() => handleEnlarge(note)}>
                                    <OpenInFullIcon fontSize="small" />
                                  </Typography>
                                </Grid>
                              </Grid>
                              <Grid container flexDirection="row" flexWrap="nowrap" justifyContent="start">
                                <Grid item xs={12}>
                                  <Box className={`${classes.quillContainer} ${classes.noBorder}`} sx={{ pr: 1, pl: 1 }}>
                                    <ReactQuill
                                      ref={(el) => (quillRefs.current[index] = el!)}
                                      theme="snow"
                                      value={validateBody(note?.body)}
                                      readOnly={true}
                                      modules={{ toolbar: false }}
                                    />
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        ))
                    ) : (
                      <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
                        <FormattedMessage defaultMessage="No notes available for this Provider" />
                      </Typography>
                    )}
                  </Box>
                </MainCard>
              ) : (
                <Box sx={{ height: notesHeight - 110 }}>
                  <MainCard sx={{ height: '100%' }} headerSX={{ padding: 2 }} contentSX={{ padding: 0, height: '100%' }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
                      <FormattedMessage defaultMessage="No Notes Available" />
                    </Typography>
                  </MainCard>
                </Box>
              )}
            </Grid>
            <Grid item xs={2.8}>
              {renderNoteList()}
            </Grid>
          </Grid>
        </Box>
      </Rnd>
      <NoteDialog note={currentNote} open={dialogOpen} onClose={handleCloseDialog} />
    </>
  );
};

export default PreviewsNoteSession;
